const API_PREFIX = import.meta.env.VITE_API_PREFIX || '/user/api';
const CSRF_HEADER = 'X-Portal-CSRF-Token';

export interface ApiError {
  code: string;
  message: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: ApiError;
}

export interface PortalPublicConfig {
  user_portal_url: string;
  portal_api_prefix: string;
  bot_username: string;
  csrf_header_name: string;
}

export interface PortalUserProfile {
  telegram_user_id: number;
  username?: string;
  first_name?: string;
  photo_url?: string;
}

export interface PortalAuthMeResponse {
  authenticated: boolean;
  user?: PortalUserProfile;
  csrf_token?: string;
}

export interface PortalSubscriptionInfo {
  subscription_tier?: string;
  subscription_until?: string;
  subscription_status: string;
  active_keys_count: number;
}

export interface PortalProfile extends PortalUserProfile {
  language: string;
  is_premium: boolean;
  subscription: PortalSubscriptionInfo;
}

export interface PortalVpnKeyItem {
  id: number;
  server_country: string;
  client_id: string;
  created_at: string;
}

export interface PortalVpnKeyConfig {
  key_id: number;
  server_code: string;
  config_text: string;
}

export interface PortalPaymentItem {
  id: string;
  period: string;
  subscription_tier?: string;
  days: number;
  amount: string;
  status: string;
  created_at: string;
}

export interface RedeemPromoResponse {
  code: string;
  bonus_days: number;
  subscription_tier: string;
  subscription_until: string;
}

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (csrfToken && options.method && options.method !== 'GET') {
    headers[CSRF_HEADER] = csrfToken;
  }

  const response = await fetch(`${API_PREFIX}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (!response.ok && response.status === 401) {
    return { success: false, error: { code: 'not_authenticated', message: 'Unauthorized' } };
  }
  return (await response.json()) as ApiResponse<T>;
}

export const api = {
  getConfig: () => request<PortalPublicConfig>('/auth/config'),
  getMe: () => request<PortalAuthMeResponse>('/auth/me'),
  loginTelegram: (payload: Record<string, unknown>) =>
    request<PortalAuthMeResponse>('/auth/telegram', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  logout: () => request<{ logged_out: boolean }>('/auth/logout', { method: 'POST' }),
  getProfile: () => request<PortalProfile>('/profile'),
  listVpnKeys: () => request<PortalVpnKeyItem[]>('/vpn/keys'),
  getVpnConfig: (keyId: number) => request<PortalVpnKeyConfig>(`/vpn/keys/${keyId}/config`),
  listPayments: () => request<PortalPaymentItem[]>('/payments'),
  redeemPromo: (code: string) =>
    request<RedeemPromoResponse>('/promos/redeem', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
};
